import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const TeamSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const partners = [
    {
      name: 'CA Abhay Jain',
      qualifications: 'FCA, DISA, M.Com., CCA, DB&F',
      designation: 'Partner',
      shortBio: 'Fellow Member of ICAI, integral part of the firm since 2004 with over two decades of expertise in statutory, bank, tax, and internal audits.',
      bio: `CA Abhay Jain is a Fellow Member of the Institute of Chartered Accountants of India (FCA) and has been an integral part of the firm since 2004 — bringing over two decades of deep professional commitment and expertise.
His areas of specialisation include statutory audits, bank audits, tax audits, internal audits, and stock audits, with extensive experience across diverse industries and client segments. Beyond his core practice, CA Abhay Jain holds a Diploma in Information Systems Audit (DISA) from ICAI, a Certification in Concurrent Audit from ICAI, and a Diploma in Banking & Finance (DB&F) — reflecting his commitment to continuous professional development.`,
      tags: ['Statutory Audit', 'Bank Audit', 'Tax Audit', 'Internal Audit', 'Stock Audit', 'DISA'],
      initials: 'AJ'
    },
    {
      name: 'CA Gaurav Bhandia',
      qualifications: 'FCA, B.Com.',
      designation: 'Partner',
      shortBio: 'Fellow Member of ICAI, associated with the firm since 2007. Previously served with KPMG Dubai, leading audits across FMCG, Retail, Manufacturing, and Real Estate sectors.',
      bio: `CA Gaurav Bhandia is a Fellow Member of the Institute of Chartered Accountants of India (FCA) and has been associated with the firm since 2007. He brings with him a distinguished international profile, having served with KPMG Dubai — one of the Big Four global accounting firms — where he led and conducted audits of large-scale companies across diverse sectors including FMCG, Retail, Manufacturing, Real Estate, and Services.
His tenure at KPMG provided him with deep, hands-on expertise in International Financial Reporting Standards (IFRS) and International Accounting Standards (IAS) — a rare and invaluable competency that enables the firm to seamlessly serve clients with cross-border operations and global reporting requirements.`,
      tags: ['KPMG Dubai', 'IFRS / IAS', 'FMCG', 'Manufacturing', 'Retail', 'Real Estate'],
      initials: 'GB'
    },
    {
      name: 'CA Gaurav Mehta',
      qualifications: 'FCA, M.Com.',
      designation: 'Partner – International Operations (Dubai, UAE)',
      shortBio: 'Fellow Member of ICAI since 2004. Spearheads the firm\'s Dubai operations, delivering cross-border advisory and financial solutions to clients with India–UAE interests.',
      bio: 'CA Gaurav Mehta is a Fellow Member of the Institute of Chartered Accountants of India since 2004. He began his career with Amlak Finance PJSC, a leading mortgage finance institution in the UAE. Since joining the firm in 2009, he has been spearheading its Dubai operations, delivering specialized cross-border advisory and financial solutions to clients with India–UAE interests, including NRIs and regional enterprises.',
      tags: ['Dubai Operations', 'UAE Advisory', 'Amlak Finance', 'Mortgage Finance', 'NRI Services', 'IFRS'],
      initials: 'GM'
    },
    {
      name: 'CA Vaibhav Agarwal',
      qualifications: 'FCA, DISA, B.Com',
      designation: 'Associate',
      shortBio: 'Fellow Member of ICAI, joined the firm in 2009. Previously Regional Head – Corporate Linked Business at ICICI Bank with expertise in Retail Credit, SME, and Debt Service Management.',
      bio: `Vaibhav joined the firm in 2009, bringing with him valuable experience from ICICI Bank Ltd, where he served as Regional Head – Corporate Linked Business. He is a Fellow Member of the Institute of Chartered Accountants of India and also holds a DISA qualification.

During his tenure at ICICI Bank, he gained extensive exposure to a wide range of financial products and segments, including Retail Credit, Small and Medium Enterprises (SME), and the Debt Service Management Group. His professional expertise spans credit evaluation, relationship management, and structured financial solutions for corporate and SME clients.

At the firm, Vaibhav plays a key role in advising clients on financial structuring, credit facilities, and business growth strategies, leveraging his strong banking and financial background.`,
      tags: ['Retail Credit', 'SME Financing', 'Debt Management', 'Credit Structuring', 'Banking Expertise'],
      initials: 'VA'
    }
  ];

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Our Team
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Led by experienced professionals committed to delivering excellence in every engagement.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {partners.map((partner, index) => (
            <div
              key={index}
              onClick={() => toggleExpand(index)}
              className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-[#C9A84C] cursor-pointer"
            >
              {/* Always visible section */}
              <div className="p-8">
                {/* Initials Avatar */}
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#b8973d] flex items-center justify-center mx-auto mb-5">
                  <span className="text-2xl font-playfair font-bold text-[#0D1B2A]">
                    {partner.initials}
                  </span>
                </div>

                {/* Name & Qualifications */}
                <h3 className="text-xl font-playfair font-bold text-[#0D1B2A] text-center mb-1">
                  {partner.name}
                </h3>
                <p className="text-sm text-[#C9A84C] text-center font-dmsans mb-1">
                  {partner.qualifications}
                </p>
                <p className="text-sm text-gray-600 text-center font-dmsans font-semibold mb-4">
                  {partner.designation}
                </p>

                {/* Short Bio - always visible */}
                <p className="text-gray-600 font-dmsans text-sm text-center leading-relaxed">
                  {partner.shortBio}
                </p>

                {/* Click to expand indicator */}
                <div className="flex items-center justify-center gap-1 mt-4 text-[#C9A84C] text-xs font-dmsans font-semibold">
                  {expandedIndex === index ? (
                    <>Show Less <ChevronUp size={14} /></>
                  ) : (
                    <>Read More <ChevronDown size={14} /></>
                  )}
                </div>
              </div>

              {/* Expandable section */}
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                expandedIndex === index ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="px-8 pb-8 border-t border-gray-100 pt-4">
                  <p className="text-gray-700 font-dmsans text-sm text-justify leading-relaxed mb-4 whitespace-pre-line">
                    {partner.bio}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {partner.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-[#FAF8F3] text-[#0D1B2A] text-xs font-dmsans rounded-full border border-[#C9A84C]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ICAI Disclaimer */}
        <div className="text-center">
          <p className="text-xs text-gray-400 font-dmsans italic max-w-4xl mx-auto">
            The details above are for informational purposes only. This does not constitute an
            advertisement or solicitation for professional work.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
